import { useAuth0 } from '@auth0/auth0-react';
import { Anchor, Box, Button, Checkbox, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { acceptCurrentTerms } from '../../api/user';
import { useUserStore } from '../../stores/userStore';
import OnboardingShell from './OnboardingShell';

const PUBLIC_SITE_URL = import.meta.env.VITE_PUBLIC_SITE_URL || 'https://thegameproject.net';

export default function TermsAcceptancePage() {
  const { getAccessTokenSilently } = useAuth0();
  const { checkUserState } = useUserStore();
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  const continueToApp = async () => {
    setSaving(true);
    try {
      await acceptCurrentTerms(getAccessTokenSilently);
      await checkUserState(getAccessTokenSilently);
    } catch {
      notifications.show({
        title: 'Could not save your agreement',
        message: 'Please try again. If the problem continues, contact support.',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingShell step={1} reassurance="Your collection and account information are private by default.">
      <Stack gap="xl">
        <div>
          <Text c="blue.7" size="xs" fw={700} tt="uppercase" lts="0.1em" mb={8}>
            Welcome to The Game Project
          </Text>
          <Title order={1} ff="'Sora Variable', 'Trebuchet MS', sans-serif" fz={{ base: 28, sm: 34 }}>
            One last thing before you begin
          </Title>
          <Text c="dimmed" mt="sm" size="lg">
            Please confirm the account terms, then you can start building your collection.
          </Text>
        </div>

        <Box p="md" bg="blue.0" style={{ border: '1px solid var(--mantine-color-blue-2)', borderRadius: 10 }}>
          <Checkbox
            checked={agreed}
            onChange={(event) => setAgreed(event.currentTarget.checked)}
            label={
              <Text size="sm">
                I confirm that I am at least 16 years old, agree to the{' '}
                <Anchor href={`${PUBLIC_SITE_URL}/terms`} target="_blank" rel="noopener noreferrer">
                  Terms of Use
                </Anchor>
                , and acknowledge the{' '}
                <Anchor href={`${PUBLIC_SITE_URL}/privacy`} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </Anchor>
                .
              </Text>
            }
            styles={{ label: { paddingInlineStart: 8 } }}
          />
        </Box>

        <Button size="md" disabled={!agreed} loading={saving} onClick={continueToApp}>
          Agree and continue
        </Button>
      </Stack>
    </OnboardingShell>
  );
}
